import { transformContent } from '@/lib/helpers/content';
import { transformJobSearchLandingPage } from '@/lib/helpers/landing';
import { transformRecommendations } from '@/lib/helpers/recommendations';
import { transformSearchLandingPage } from '@/lib/helpers/search';
import { transformSite } from '@/lib/helpers/site';
import omit from 'lodash/omit';
import React from 'react';
import { Opportunity } from '../../types/content';

/** Resolve field_city UUIDs to { label } using category_cities lookup.
 *  If items are already objects with a label (from GraphQL subfield selection), pass through. */
function resolveCityRefs(opportunity: any, cityNodes?: { id: string; drupal_id: string; label: string }[]): any {
  const cityRefs = opportunity?.relationships?.field_city;
  if (!Array.isArray(cityRefs) || !cityRefs.length) return opportunity;

  // Already resolved objects with labels — no lookup needed
  if (typeof cityRefs[0] === 'object' && cityRefs[0]?.label != null) return opportunity;

  if (!cityNodes?.length) return opportunity;

  const idToLabel = Object.fromEntries(cityNodes.map((c) => [c.id, c.label]));
  const resolved = cityRefs
    .map((id: string) => {
      const label = idToLabel[id];
      return label != null ? { label } : null;
    })
    .filter(Boolean);

  return {
    ...opportunity,
    relationships: {
      ...opportunity.relationships,
      field_city: resolved
    }
  };
}

export function withDataTransformer(Component: any) {
  return function withDataTransformerWrapper(props: any) {
    let data: any = structuredClone(props?.data, {});

    if (data?.site) data.site = transformSite(data.site);
    if (data?.gtiRecommendation) data.gtiRecommendation = transformRecommendations(data.gtiRecommendation);

    if (data?.opportunity) {
      data.opportunity = transformContent<Opportunity>(
        resolveCityRefs(data.opportunity, data.allCategoryCities?.nodes)
      );
    }
    if (data?.opportunities)
      data.opportunities = data.opportunities?.nodes?.map((opportunity: any) =>
        transformContent<Opportunity>(opportunity)
      );

    if (data?.searchLandingPages)
      data.searchLandingPages = data.searchLandingPages?.nodes?.map((searchLandingPage: any) =>
        transformSearchLandingPage(searchLandingPage)
      );
    if (data?.cityLandingPages)
      data.cityLandingPages = data.cityLandingPages?.nodes?.map((searchLandingPage: any) =>
        transformSearchLandingPage(searchLandingPage)
      );
    if (data?.jobSearchLandingPage)
      data.jobSearchLandingPage = transformJobSearchLandingPage(data.jobSearchLandingPage);

    return <Component {...omit(props, ['data'])} data={data} />;
  };
}
